"use client"

import * as React from "react"
import { AlertTriangleIcon, CircleHelpIcon } from "lucide-react"

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  HelperMessage,
  Label,
  Textarea,
  cn,
} from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { MobileFormError, MobileFormSection, MobileFormSheet, MobileFormTextArea } from "@/shared/components/mobile-form"
import { useIsMobile } from "@/shared/hooks/use-mobile"

interface ReconciliationTransitionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  confirmLabel: string
  /** Shown above the note field. Omit the field entirely by leaving `noteLabel` undefined. */
  noteLabel?: string
  notePlaceholder?: string
  /** When true the confirm button stays disabled until a note is written. */
  noteRequired?: boolean
  destructive?: boolean
  pending?: boolean
  onConfirm: (note: string | undefined) => void
}

/**
 * A confirm dialog that can also collect the note a transition requires.
 *
 * Separate from {@link ConfirmActionDialog} because two of the four reconciliation transitions cannot proceed
 * without prose: a rejection with no reason is not actionable, and an unexplained difference is exactly what
 * must not pass silently. The backend enforces both — this exists so the user finds out before they click,
 * rather than through a 400 that reads like a bug.
 */
export function ReconciliationTransitionDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  noteLabel,
  notePlaceholder,
  noteRequired = false,
  destructive = false,
  pending = false,
  onConfirm,
}: ReconciliationTransitionDialogProps) {
  const t = useTranslations("finance.reconciliation")
  const [note, setNote] = React.useState("")
  const [touched, setTouched] = React.useState(false)

  // Reset between openings so a withdrawn draft's text never resurfaces on the next action.
  React.useEffect(() => {
    if (!open) return
    setNote("")
    setTouched(false)
  }, [open])

  const trimmed = note.trim()
  const blocked = noteRequired && trimmed.length === 0
  // Only complain once they have actually engaged and left it empty. Firing on open scolds someone for not
  // having typed yet, and the disabled confirm button already says the field is needed.
  const showRequiredWarning = blocked && touched
  const Icon = destructive ? AlertTriangleIcon : CircleHelpIcon
  const isMobile = useIsMobile()

  if (isMobile) {
    // Phone: the app's bottom sheet, the question under its title and the note as a grouped text row.
    return (
      <MobileFormSheet
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        cancelLabel={t("dialogs.cancel")}
        saveLabel={confirmLabel}
        onSave={() => onConfirm(trimmed.length > 0 ? trimmed : undefined)}
        saving={pending}
        saveDisabled={blocked}
        destructive={destructive}
      >
        <p className='px-1 text-[15px] font-semibold text-muted-foreground'>{description}</p>
        {noteLabel ? (
          <div>
            <MobileFormSection title={noteLabel}>
              <MobileFormTextArea
                value={note}
                placeholder={notePlaceholder}
                aria-label={noteLabel}
                aria-required={noteRequired}
                aria-invalid={showRequiredWarning}
                onChange={(event) => setNote(event.target.value)}
                onBlur={() => setTouched(true)}
              />
            </MobileFormSection>
            <MobileFormError message={showRequiredWarning ? t("dialogs.noteRequired") : null} />
          </div>
        ) : null}
      </MobileFormSheet>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size='md' className='gap-3 overflow-hidden' mobileFullscreen={false}>
        {/* Icon + title are the heading. The description is BODY text at full width below it — squeezed into
            the icon's column it reads as a subtitle and wraps into a narrow ragged block while the dialog has
            room to spare. */}
        <div className='flex items-center gap-3'>
          <div
            className={cn(
              "flex size-9 shrink-0 items-center justify-center rounded-full border",
              destructive
                ? "bg-destructive/12 text-destructive border-destructive/25"
                : "bg-primary/12 text-primary border-primary/25",
            )}
          >
            <Icon className='size-4' />
          </div>
          <DialogTitle className='text-base leading-tight font-semibold'>{title}</DialogTitle>
        </div>

        <DialogDescription className='text-muted-foreground text-sm'>{description}</DialogDescription>

        {noteLabel ? (
          <div className='space-y-1.5'>
            <Label htmlFor='reconciliation-transition-note' className='text-sm font-medium'>
              {noteLabel}
              {/* Says "you must fill this in" up front, instead of waiting to tell you off afterwards. */}
              {noteRequired ? <span className='text-destructive ml-0.5'>*</span> : null}
            </Label>
            <Textarea
              id='reconciliation-transition-note'
              rows={3}
              value={note}
              placeholder={notePlaceholder}
              aria-required={noteRequired}
              aria-invalid={showRequiredWarning}
              onChange={(event) => setNote(event.target.value)}
              onBlur={() => setTouched(true)}
            />
            {showRequiredWarning ? (
              <HelperMessage tone='warning' size='sm'>
                {t("dialogs.noteRequired")}
              </HelperMessage>
            ) : null}
          </div>
        ) : null}

        <DialogFooter className='gap-2'>
          <Button size='sm' variant='outline' onClick={() => onOpenChange(false)} disabled={pending}>
            {t("dialogs.cancel")}
          </Button>
          <Button
            size='sm'
            variant={destructive ? "destructive" : "default"}
            disabled={pending || blocked}
            onClick={() => onConfirm(trimmed.length > 0 ? trimmed : undefined)}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
